import React, { useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Search, Package, Check, Truck, AlertCircle, Clock, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
    pending: { icon: Clock, label: "Awaiting payment", color: "bg-amber-100 text-amber-800", desc: "We're waiting for your bank transfer to clear. Please use your order number as the reference." },
    confirmed: { icon: Check, label: "Order confirmed", color: "bg-blue-100 text-blue-800", desc: "Your order is confirmed and queued for fulfilment." },
    paid: { icon: Check, label: "Payment received", color: "bg-emerald-100 text-emerald-800", desc: "Payment confirmed. Your order is being prepared and will ship within 1-2 business days." },
    shipped: { icon: Truck, label: "Shipped", color: "bg-purple-100 text-purple-800", desc: "Your order is on the way. Look out for it in the post." },
    cancelled: { icon: X, label: "Cancelled", color: "bg-red-100 text-red-800", desc: "This order has been cancelled. Reach out if this is unexpected." },
};

export default function TrackOrder() {
    const [params] = useSearchParams();
    const [orderNumber, setOrderNumber] = useState(params.get("o") || "");
    const [email, setEmail] = useState(params.get("e") || "");
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!orderNumber.trim() || !email.trim()) {
            toast.error("Enter both your order number and email");
            return;
        }
        setLoading(true);
        setNotFound(false);
        setOrder(null);
        try {
            const res = await axios.get(`${API}/orders/lookup/${orderNumber.trim()}`);
            // Verify email matches
            if (res.data.customer?.email?.toLowerCase() !== email.trim().toLowerCase()) {
                setNotFound(true);
                return;
            }
            setOrder(res.data);
        } catch (err) {
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const meta = order ? STATUS_META[order.status] || STATUS_META.pending : null;
    const StatusIcon = meta?.icon || Package;

    return (
        <div className="pt-32 pb-24 min-h-screen" data-testid="track-order-page">
            <div className="max-w-3xl mx-auto px-6 md:px-12">
                <p className="text-pep-primary text-sm uppercase tracking-widest font-medium mb-3">Order Tracking</p>
                <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter text-pep-navy font-medium mb-4">
                    Track your order.
                </h1>
                <p className="text-pep-muted text-lg mb-12 max-w-xl">
                    Enter your order number and the email you used at checkout to see the current status of your order.
                </p>

                <form onSubmit={submit} className="bg-white rounded-3xl border border-pep-border/50 p-6 md:p-8 mb-8" data-testid="track-form">
                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="text-sm text-pep-navy font-medium mb-1.5 block">Order number</label>
                            <Input
                                value={orderNumber}
                                onChange={(e) => setOrderNumber(e.target.value)}
                                placeholder="PS-YYYYMMDD-XXXXXX"
                                className="rounded-xl border-pep-border"
                                data-testid="track-order-number"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-pep-navy font-medium mb-1.5 block">Email</label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@lab.com"
                                className="rounded-xl border-pep-border"
                                data-testid="track-email"
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-pep-primary text-white rounded-full py-3.5 font-medium hover:bg-pep-navy transition disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="track-submit"
                    >
                        {loading ? "Looking up..." : (<><Search className="w-4 h-4" /> Track Order</>)}
                    </button>
                </form>

                {notFound && (
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-3" data-testid="track-not-found">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-900 leading-relaxed">
                            We couldn't find an order matching that order number and email. Double-check both, or
                            <Link to="/contact" className="text-pep-primary underline ml-1">contact us</Link>.
                        </div>
                    </div>
                )}

                {order && meta && (
                    <div className="bg-white rounded-3xl border border-pep-border/50 p-8" data-testid="track-result">
                        <div className="flex flex-wrap justify-between items-start gap-4 pb-6 border-b border-pep-border/50 mb-6">
                            <div>
                                <p className="text-xs uppercase tracking-widest text-pep-muted">Order number</p>
                                <p className="font-heading text-2xl text-pep-navy" data-testid="track-order-id">{order.order_number}</p>
                                <p className="text-xs text-pep-muted mt-1">Placed {new Date(order.created_at).toLocaleString("en-AU")}</p>
                            </div>
                            <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium ${meta.color}`} data-testid="track-status">
                                <StatusIcon className="w-4 h-4" />
                                {meta.label}
                            </div>
                        </div>

                        <p className="text-pep-navy text-base leading-relaxed mb-6">{meta.desc}</p>

                        <div className="space-y-2 mb-6">
                            <p className="text-xs uppercase tracking-widest text-pep-muted mb-3">Items</p>
                            {order.items.map((i, idx) => (
                                <div key={idx} className="flex justify-between text-sm">
                                    <span className="text-pep-navy">{i.name} × {i.quantity}</span>
                                    <span className="text-pep-muted">${(i.price * i.quantity).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>

                        <div className="space-y-1 pt-4 border-t border-pep-border/50">
                            <div className="flex justify-between text-sm">
                                <span className="text-pep-muted">Subtotal</span>
                                <span className="text-pep-navy">${order.subtotal.toFixed(2)}</span>
                            </div>
                            {order.discount_amount > 0 && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-pep-primary">Bundle discount ({order.bundle_discount_percent}%)</span>
                                    <span className="text-pep-primary">−${order.discount_amount.toFixed(2)}</span>
                                </div>
                            )}
                            {typeof order.shipping_cost === "number" && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-pep-muted">Shipping</span>
                                    <span className="text-pep-navy">{order.shipping_cost === 0 ? "FREE" : `$${order.shipping_cost.toFixed(2)}`}</span>
                                </div>
                            )}
                            <div className="flex justify-between font-heading text-xl pt-3 border-t border-pep-border/50">
                                <span className="text-pep-navy">Total</span>
                                <span className="text-pep-navy">${order.total.toFixed(2)}</span>
                            </div>
                        </div>

                        {order.status === "pending" && (
                            <div className="mt-6 p-4 rounded-2xl bg-pep-surface border-l-4 border-pep-primary text-sm text-pep-navy">
                                <strong>Awaiting bank transfer.</strong> Please use <strong className="text-pep-primary">{order.order_number}</strong> as your reference.
                                Your order will ship as soon as payment lands.
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
